import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import React, { type FormEvent, useEffect } from "react";

import type {
  OverlayState,
  UserFormValues,
  UserRecord,
} from "../../../features/users/userTypes";
import {
  isValidUsername,
  normalizeUsername,
} from "../../../features/users/username";
import {
  USER_DEPARTMENT_OPTIONS,
  USER_EMPLOYEE_TYPE_OPTIONS,
} from "../../../shared/userReferenceCodes";
import { classNames } from "../../../shared/utility";
import Input from "../../controls/Input";
import SelectMenu from "../../controls/SelectMenu";
import { useForm } from "../useForm";

const initialValues: UserFormValues = {
  name: "",
  username: "",
  email: "",
  user_type: { value: 0, label: "Employee" },
  department: [],
  employee_type: null,
  current_password: "",
  new_password: "",
  new_password_confirmation: "",
};

const userOptions = [
  { value: 0, label: "Employee" },
  { value: 1, label: "Admin" },
];

interface UserDetailsFormProps {
  user?: UserRecord;
  setOpen: (state: OverlayState) => void;
  actionCall: (values: UserFormValues) => void;
  isEdit: boolean;
}

export default function UserDetailsForm({
  user,
  setOpen,
  actionCall,
  isEdit,
}: UserDetailsFormProps) {
  const {
    values,
    setValues,
    errors: rawErrors,
    setErrors,
    handleInputChange,
  } = useForm(initialValues, false);
  const errors = rawErrors as Partial<
    Record<keyof UserFormValues, string | null>
  >;

  const validate = (): UserFormValues | null => {
    const username = normalizeUsername(values.username);
    const nextErrors: Record<string, string | null> = {
      name: values.name.trim() ? null : "This field is required.",
      username: isValidUsername(username)
        ? null
        : "Use 3–64 letters, numbers, periods, underscores, or hyphens.",
      email: values.email.trim() ? null : "This field is required.",
      department:
        values.department.length > 0 ? null : "This field is required.",
      employee_type: values.employee_type?.value
        ? null
        : "This field is required.",
      new_password: null,
      new_password_confirmation: null,
    };

    if (!isEdit) {
      if (!values.new_password) {
        nextErrors.new_password = "This field is required.";
      }
      if (!values.new_password_confirmation) {
        nextErrors.new_password_confirmation = "This field is required.";
      } else if (values.new_password_confirmation !== values.new_password) {
        nextErrors.new_password_confirmation = "The passwords do not match.";
      }
    }

    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return null;

    return { ...values, username };
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const normalizedValues = validate();
    if (normalizedValues) actionCall(normalizedValues);
  };

  useEffect(() => {
    if (isEdit && user) {
      setValues({
        ...initialValues,
        ...user,
      });
    }
  }, [isEdit, setValues, user]);

  const close = () => setOpen({ childComponent: null, open: false });

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={handleSubmit}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll py-6">
        <div className="px-4 sm:px-6">
          <div className="flex items-start justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              {isEdit ? `Edit ${user?.name ?? "user"}` : "Add new user"}
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                onClick={close}
              >
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
        <div className="relative mt-6 flex-1 px-4 sm:px-6">
          <div className="mt-2">
            <Input
              label="Name"
              name="name"
              onChange={handleInputChange}
              value={values.name}
              error={errors.name}
              labelOn={true}
              type="text"
            />
          </div>
          <div className="mt-2">
            <Input
              label="Username"
              name="username"
              onChange={handleInputChange}
              value={values.username}
              error={errors.username}
              labelOn={true}
              type="text"
              autoCapitalize="none"
              autoCorrect="off"
            />
            <p className="mt-1 text-xs text-gray-500">
              Used with the company domain when signing in on mobile.
            </p>
          </div>
          <div className="mt-2">
            <Input
              label="Email"
              name="email"
              onChange={handleInputChange}
              value={values.email}
              error={errors.email}
              labelOn={true}
              type="email"
            />
          </div>
          <div className="mt-2">
            <SelectMenu
              handleInputChange={handleInputChange}
              name="user_type"
              options={userOptions}
              value={values.user_type}
              title="User Type"
              error={errors.user_type}
              showLabel={true}
              placeholder="Select..."
            />
          </div>
          <div className="mt-2">
            <SelectMenu
              handleInputChange={handleInputChange}
              name="department"
              options={USER_DEPARTMENT_OPTIONS}
              value={values.department}
              title="Department"
              error={errors.department}
              showLabel={true}
              isMulti
              placeholder="Select..."
            />
          </div>
          <div className="mt-2">
            <SelectMenu
              handleInputChange={handleInputChange}
              name="employee_type"
              options={USER_EMPLOYEE_TYPE_OPTIONS}
              value={values.employee_type}
              title="Employee type"
              error={errors.employee_type}
              showLabel={true}
              placeholder="Select..."
            />
          </div>
          <div className={classNames(isEdit ? "hidden" : "mt-2")}>
            <Input
              label="New Password"
              name="new_password"
              onChange={handleInputChange}
              value={values.new_password}
              error={errors.new_password}
              labelOn={true}
              type="password"
            />
          </div>
          <div className={classNames(isEdit ? "hidden" : "mt-2")}>
            <Input
              label="Confirm password"
              name="new_password_confirmation"
              onChange={handleInputChange}
              value={values.new_password_confirmation}
              error={errors.new_password_confirmation}
              labelOn={true}
              type="password"
            />
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
          onClick={close}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600"
        >
          {isEdit ? "Save" : "Create"}
        </button>
      </div>
    </form>
  );
}
